import Link from "@mui/material/Link";
import {
  BooleanField,
  FunctionField,
  Identifier,
  NumberField,
  TextField,
  useDataProvider,
  useRecordContext,
} from "react-admin";
import { MutationFunction, MutationKey } from "react-query";

import {
  Action,
  CustomDataProvider,
  DataKind,
  getRegionalResourceName,
  MicrosoftTokenResponse,
  Region,
  Resource,
  ResourceRegional,
  SyncErrorResponse,
  UserResponse,
} from "../../types";
import {
  ActionMenu,
  Authorize,
  CustomDatagrid,
  CustomDateTimeField,
  CustomReferenceField,
  CustomReferenceManyField,
  CustomShow,
  MutationActionMenuItem,
  ProjectsForSync,
  ShowTable,
} from "../custom";

interface ClearSubscriptionMutationFnVariables {
  id: Identifier;
}
interface DisconnectProjectsMutationFnVariables {
  id: Identifier;
}

interface ResetDeltaLinkMutationFnVariables {
  id: Identifier;
}

interface RevokeTokenMutationFnVariables {
  id: Identifier;
}

function ClearSubscription(props: { region: Region }) {
  const record = useRecordContext<MicrosoftTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: MicrosoftTokenResponse },
      ClearSubscriptionMutationFnVariables
    > = (variables: ClearSubscriptionMutationFnVariables) => {
      return dataProvider.clearSubscriptionForMicrosoftToken(
        props.region,
        variables.id,
      );
    };
    const mutationKey: MutationKey = [
      "clearSubscriptionForMicrosoftToken",
      record.id,
    ];
    const successMessage = "Cleared subscription ID successfully! ✅";

    return (
      <MutationActionMenuItem
        label="Clear Subscription"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function DisconnectProjects(props: { region: Region }) {
  const record = useRecordContext<MicrosoftTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: MicrosoftTokenResponse },
      DisconnectProjectsMutationFnVariables
    > = (variables: DisconnectProjectsMutationFnVariables) => {
      return dataProvider.disconnectProjectsForMicrosoftToken(
        props.region,
        variables.id,
      );
    };
    const mutationKey: MutationKey = [
      "disconnectProjectsForMicrosoftToken",
      record.id,
    ];
    const successMessage = `Disconnected all projects connected to the Microsoft token: ${record.id}. ✅`;

    return (
      <MutationActionMenuItem
        label="Disconnect Projects"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function ResetDeltaLink(props: { region: Region }) {
  const record = useRecordContext<MicrosoftTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: MicrosoftTokenResponse },
      ResetDeltaLinkMutationFnVariables
    > = (variables: ResetDeltaLinkMutationFnVariables) => {
      return dataProvider.resetDeltaLink(props.region, variables.id);
    };
    const mutationKey: MutationKey = ["resetDeltaLink", record.id];
    const successMessage = "Token delta link reset successfully! ✅";

    return (
      <MutationActionMenuItem
        label="Reset Delta Link"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function RevokeToken(props: { region: Region }) {
  const record = useRecordContext<MicrosoftTokenResponse>();
  const dataProvider = useDataProvider<CustomDataProvider>();

  if (record) {
    const mutationFn: MutationFunction<
      { data: MicrosoftTokenResponse },
      RevokeTokenMutationFnVariables
    > = (variables: RevokeTokenMutationFnVariables) => {
      return dataProvider.revokeMicrosoftToken(props.region, variables.id);
    };
    const mutationKey: MutationKey = ["revokeMicrosoftToken", record.id];
    const successMessage = "Token revoked successfully! ✅";

    return (
      <MutationActionMenuItem
        label="Revoke Token"
        mutationFn={mutationFn}
        mutationKey={mutationKey}
        mutationVariables={{ id: record.id }}
        successMessage={successMessage}
      />
    );
  } else {
    return null;
  }
}

function MicrosoftTokenShow(props: { region: Region }) {
  const fieldwireActions = [
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.MicrosoftToken,
      element: <DisconnectProjects region={props.region} />,
    },
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.MicrosoftToken,
      element: <ResetDeltaLink region={props.region} />,
    },
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.MicrosoftToken,
      element: <ClearSubscription region={props.region} />,
    },
    {
      checkPermissionForAction: Action.Update,
      checkPermissionForDataKind: DataKind.MicrosoftToken,
      element: <RevokeToken region={props.region} />,
    },
  ];

  return (
    <CustomShow
      checkPermissionFor={DataKind.MicrosoftToken}
      displayDelete={false}
      displayEdit={false}
      fieldwireActions={<ActionMenu fieldwireActions={fieldwireActions} />}
    >
      {/* START OF MICROSOFT TOKEN DETAILS */}
      <ShowTable title="Microsoft Token Details">
        <CustomReferenceField
          label="User"
          source="user_id"
          reference={Resource.User}
        >
          <FunctionField
            render={(record: UserResponse) =>
              `${record.first_name} ${record.last_name}`
            }
          />
        </CustomReferenceField>
        <BooleanField label="Is Valid" source="is_valid" />
        <CustomDateTimeField label="Last Pulled At" source="last_pulled_at" />
        <CustomDateTimeField label="Last Pushed At" source="last_pushed_at" />
        <CustomDateTimeField label="Created At" source="created_at" />
        <CustomDateTimeField label="Updated At" source="updated_at" />
        <CustomDateTimeField label="Expires At" source="expires_at" />
        <CustomDateTimeField
          label="Subscription Expiration"
          source="subscription_expiration"
        />
        <TextField label="Email" source="email" />
        <TextField label="Delta Link" source="delta_link" />
      </ShowTable>
      {/* END OF MICROSOFT TOKEN DETAILS */}

      {/* START OF PROJECTS */}
      <Authorize
        action={Action.Read}
        dataKind={DataKind.MicrosoftToken}
        disableUnauthorizedMessage
      >
        <ProjectsForSync
          region={props.region}
          targetRegional={ResourceRegional.MicrosoftToken}
        />
      </Authorize>
      {/* END OF PROJECTS */}

      {/* START OF SYNC ERRORS */}
      {/* Backend enforces the ordering for sync errors - update_at_desc */}
      <Authorize action={Action.Read} dataKind={DataKind.MicrosoftToken}>
        <ShowTable rowFlexDirection="column" title="Sync Errors">
          <CustomReferenceManyField
            displayPagination={true}
            label={false}
            reference={getRegionalResourceName(
              props.region,
              ResourceRegional.SyncError,
            )}
            target={getRegionalResourceName(
              props.region,
              ResourceRegional.MicrosoftToken,
            )}
          >
            <CustomDatagrid>
              <CustomDateTimeField
                label="Last Occurred"
                source="updated_at"
                sortable={false}
              />
              <CustomDateTimeField
                label="First Occurred"
                source="created_at"
                sortable={false}
              />
              <CustomReferenceField
                label="User"
                source="sync_owner_id"
                reference={Resource.User}
                sortable={false}
              >
                <TextField source="email" />
              </CustomReferenceField>
              <NumberField label="Count" source="count" sortable={false} />
              <TextField
                label="Entity Type"
                source="entity_type"
                sortable={false}
              />
              <TextField
                label="Entity Name"
                source="entity_name"
                sortable={false}
              />
              <TextField
                label="Entity Id"
                source="entity_id"
                sortable={false}
              />
              <FunctionField
                label="Error (May Link to Rollbar)"
                render={(record: SyncErrorResponse) =>
                  record.error_url ? (
                    <Link
                      href={record.error_url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {record.error_message}
                    </Link>
                  ) : (
                    record.error_message
                  )
                }
                sortable={false}
              />
            </CustomDatagrid>
          </CustomReferenceManyField>
        </ShowTable>
      </Authorize>
      {/* END OF SYNC ERRORS */}
    </CustomShow>
  );
}

export default MicrosoftTokenShow;
